// FCM 푸시 등록 — 권한 요청 → 등록 → 토큰 upsert(회전 시 이전 문서 삭제).
//
// iOS에서 @capacitor/push-notifications의 'registration' 이벤트가 넘겨주는 값은, AppDelegate에 붙인
// Firebase Messaging 브리지(§AppDelegate.swift) 덕분에 raw APNs device token이 아니라 FCM registration token이다.
// 이 파일은 그 FCM 토큰만 다룬다.
//
// 웹/비네이티브에서는 전부 no-op(권한 프롬프트를 띄우지 않음).

import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { db } from './firebase'
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { setLastFcmToken, getLastFcmToken, onFcmToken } from './pushToken'
import { setUserProperty, trackEvent } from './analytics'

const LAST_TOKEN_KEY = 'suguide-last-fcm-token'
const DENIED_KEY = 'suguide-push-denied'
const INSTALLATION_ID_KEY = 'suguide-installation-id'
const PERMISSION_STATUS_KEY = 'suguide-push-permission-status'

let listenersAttached = false
let listenerSetup = null
let currentUid = null
let installationMetadata = null
let tokenGeneration = 0
let tokenOperations = Promise.resolve()
const writtenTokens = new Map()

function safeGet(k) { try { return localStorage.getItem(k) } catch { return null } }
function safeSet(k, v) { try { localStorage.setItem(k, v) } catch { /* ignore */ } }
function safeRemove(k) { try { localStorage.removeItem(k) } catch { /* ignore */ } }

function fallbackInstallationId() {
  const stored = safeGet(INSTALLATION_ID_KEY)
  if (stored) return stored
  const generated = globalThis.crypto?.randomUUID?.() || `install-${Date.now()}-${Math.random().toString(36).slice(2)}`
  safeSet(INSTALLATION_ID_KEY, generated)
  return generated
}

async function getInstallationMetadata() {
  if (installationMetadata) return installationMetadata
  try {
    const [id, info] = await Promise.all([Device.getId(), Device.getInfo()])
    installationMetadata = {
      installationId: id?.identifier || fallbackInstallationId(),
      deviceName: info?.name || info?.model || null,
    }
  } catch {
    installationMetadata = { installationId: fallbackInstallationId(), deviceName: null }
  }
  return installationMetadata
}

function enqueueTokenOperation(operation) {
  const result = tokenOperations.then(operation)
  tokenOperations = result.catch(() => {})
  return result
}
function upsertToken(fcmToken) {
  const uid = currentUid, generation = tokenGeneration
  return enqueueTokenOperation(() => persistToken(fcmToken, uid, generation))
}
async function persistToken(fcmToken, uid, generation) {
  const isCurrent = () => uid === currentUid && generation === tokenGeneration
  if (!isCurrent()) return false
  const prev = safeGet(LAST_TOKEN_KEY)
  const metadata = installationMetadata || await getInstallationMetadata()
  let saved = !uid || !db
  if (uid && db) {
    try {
      const previousDevice = prev
        ? (await getDoc(doc(db, 'users', uid, 'deviceTokens', prev)).catch(() => null))?.data() || null
        : null
      if (!isCurrent()) return false
      if (prev && prev !== fcmToken) {
        await deleteDoc(doc(db, 'users', uid, 'deviceTokens', prev)).catch(() => {})
      }
      if (!isCurrent()) return false
      if (!writtenTokens.has(uid)) writtenTokens.set(uid, new Set())
      writtenTokens.get(uid).add(fcmToken)
      await setDoc(doc(db, 'users', uid, 'deviceTokens', fcmToken), {
        token: fcmToken,
        platform: Capacitor.getPlatform(),
        installationId: metadata.installationId,
        deviceName: metadata.deviceName,
        isTestDevice: previousDevice?.isTestDevice === true,
        enabled: true,
        updatedAt: serverTimestamp(),
      }, { merge: true })
      saved = true
    } catch {
      console.warn('[push] token upsert failed')
    }
  }
  if (!isCurrent()) return false
  safeSet(LAST_TOKEN_KEY, fcmToken)
  if (uid) trackEvent('push_token_registered', { platform: Capacitor.getPlatform(), result: saved ? 'success' : 'failure' })
  return saved
}

async function attachListeners(PushNotifications) {
  if (listenersAttached) return
  if (listenerSetup) return listenerSetup
  listenerSetup = (async () => {
    const handles = []
    try {
      handles.push(await PushNotifications.addListener('registration', (token) => {
        const fcmToken = token?.value
        if (!fcmToken) return
        setLastFcmToken(fcmToken)
        void upsertToken(fcmToken)
      }))
      handles.push(await PushNotifications.addListener('registrationError', () => {
        console.warn('[push] registration failed')
      }))
      listenersAttached = true
    } catch (error) {
      await Promise.all(handles.map(handle => Promise.resolve(handle?.remove?.()).catch(() => {})))
      throw error
    } finally {
      listenerSetup = null
    }
  })()
  return listenerSetup
}

/**
 * 권한 요청 + 등록. uid를 넘기면 그 사용자의 deviceTokens에 토큰을 upsert한다(로그인 시).
 * uid 없이 호출하면(익명) 토큰은 getLastFcmToken()에만 담기고 Firestore엔 쓰지 않는다.
 * 권한 거부 시 조용히 종료(재프롬프트 스팸 방지 플래그 기록).
 */
export async function registerPushNotifications(uid = null, options = {}) {
  try {
    return await registerPushNotificationsImpl(uid, options)
  } catch {
    // Native plugin failures must not leave startup callers with a rejected promise.
    console.warn('[push] initialization unavailable')
    return 'unknown'
  }
}

async function registerPushNotificationsImpl(uid = null, { requestPermission = true, source = 'app_start' } = {}) {
  if (!Capacitor.isNativePlatform()) return 'unknown'
  if (uid && currentUid && uid !== currentUid) await unregisterCurrentDeviceToken(currentUid)
  currentUid = uid ?? currentUid
  const { PushNotifications } = await import('@capacitor/push-notifications')

  let perm = await PushNotifications.checkPermissions()
  let requested = false
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    if (!requestPermission) {
      setUserProperty('push_enabled', 'unknown')
      return 'unknown'
    }
    trackEvent('push_permission_requested', { source })
    trackEvent('notification_permission_prompted', { source })
    requested = true
    perm = await PushNotifications.requestPermissions()
  }
  const status = perm.receive === 'granted'
    ? 'granted'
    : perm.receive === 'provisional'
      ? 'provisional'
      : perm.receive === 'denied'
        ? 'denied'
        : 'unknown'
  if (requested) {
    trackEvent('push_permission_result', { permission_status: status, source })
    if (status === 'granted' || status === 'provisional') trackEvent('notification_permission_granted', { source })
    if (status === 'denied') trackEvent('notification_permission_denied', { source })
  }
  setUserProperty('push_enabled', status === 'granted' || status === 'provisional' ? 'yes' : status === 'denied' ? 'no' : 'unknown')

  if (status !== 'granted' && status !== 'provisional') {
    safeSet(DENIED_KEY, '1')
    if (safeGet(PERMISSION_STATUS_KEY) !== status) {
      safeSet(PERMISSION_STATUS_KEY, status)
      // Transitional legacy event: its old schema cannot represent provisional.
      if (status !== 'provisional') trackEvent('push_permission_status', { status, platform: Capacitor.getPlatform() })
    }
    return status
  }
  safeRemove(DENIED_KEY)
  if (safeGet(PERMISSION_STATUS_KEY) !== status) {
    safeSet(PERMISSION_STATUS_KEY, status)
    if (status === 'granted') trackEvent('push_permission_status', { status: 'granted', platform: Capacitor.getPlatform() })
  }
  await getInstallationMetadata()
  await attachListeners(PushNotifications)

  const cachedToken = getLastFcmToken() || safeGet(LAST_TOKEN_KEY)
  if (currentUid && cachedToken) {
    setLastFcmToken(cachedToken)
    await upsertToken(cachedToken)
  }

  try {
    await PushNotifications.register()
  } catch {
    // Token provisioning failure does not revoke an already granted permission.
    console.warn('[push] registration unavailable')
  }
  return status
}

/**
 * FCM 토큰을 보장(익명 제보 알림 등록 등에 사용). 이미 있으면 즉시 반환, 없으면 등록을 트리거하고
 * 'registration' 이벤트로 토큰이 올 때까지(또는 timeout까지) 기다린다. 네이티브가 아니면 null.
 */
export async function ensureFcmToken(uid = null, { timeoutMs = 8000 } = {}) {
  if (!Capacitor.isNativePlatform()) return null
  const existing = getLastFcmToken()
  if (existing) return existing
  await registerPushNotifications(uid)
  return await new Promise((resolve) => {
    let settled = false
    const unsub = onFcmToken((t) => {
      if (!settled && t) { settled = true; unsub(); resolve(t) }
    })
    setTimeout(() => { if (!settled) { settled = true; unsub(); resolve(getLastFcmToken()) } }, timeoutMs)
  })
}

/** 로그아웃 시 이 기기의 현재 토큰 문서를 삭제(다른 기기 토큰은 건드리지 않음). */
export async function unregisterCurrentDeviceToken(uid) {
  const previousToken = safeGet(LAST_TOKEN_KEY)
  currentUid = null
  tokenGeneration++
  // A write already accepted by Firestore must finish before its logout cleanup.
  return enqueueTokenOperation(async () => {
    const tokens = new Set([previousToken, ...(writtenTokens.get(uid) ?? [])].filter(Boolean))
    if (uid && db) {
      for (const token of tokens) {
        try { await deleteDoc(doc(db, 'users', uid, 'deviceTokens', token)) } catch { /* retry on next registration */ }
      }
    }
    writtenTokens.delete(uid)
  })
}

export function resetPushRegistrationForTesting() {
  listenersAttached = false
  listenerSetup = null
  currentUid = null
  installationMetadata = null
  tokenGeneration++
  tokenOperations = Promise.resolve()
  writtenTokens.clear()
}
